import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { getCurrentWeek, formatWeekDateRangeCompact } from '../utils/weekUtils';
import NotesModal from '../modals/NotesModal';
import { upsertNote } from '../ducks/plannerActions';
import { getColorOptionForBlock } from './blockColors';
import useReviewViewScroll from '../hooks/useReviewViewScroll';
import useNetworkStatus from '../../../shared/utils/networkStatus';
import iconSet from '../../../shared/images/teambuildr-selection.json';

const ReviewViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px;
  padding-top: 16px;
  font-family: 'Nunito Sans', sans-serif;
  color: #444444;
  background: #fcfcfc;
  box-sizing: border-box;
  position: relative;
  flex: 1;
  min-height: 0;
  gap: 8px;
`;

const ReviewSurface = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #ffffff;
  overflow: visible;
  flex: 1;
  min-height: 0;
  position: relative;
  border: 1px solid #eeeeee;
  border-radius: 0;
`;

const TimelineScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  position: relative;
  padding-left: 4px;
  margin-left: -4px;
`;

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const TableHeader = styled.div`
  display: flex;
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 20;
  background: #ffffff;
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const WeekHeaderCell = styled.div`
  width: 210px;
  min-width: 140px;
  padding: 8px;
  background: #ffffff;
  border-right: 1px solid #eeeeee;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  color: #444444;
  font-family: 'Nunito Sans', sans-serif;
  line-height: 1.5;

  @media (max-width: 900px) {
    width: 160px;
  }
`;

const BlocksHeaderCell = styled.div`
  flex: 1;
  padding: 8px;
  background: #ffffff;
  border-right: 1px solid #eeeeee;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  color: #444444;
  font-family: 'Nunito Sans', sans-serif;
  line-height: 1.5;
`;

const NotesHeaderCell = styled.div`
  width: 300px;
  min-width: 200px;
  padding: 8px;
  background: #ffffff;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  color: #444444;
  font-family: 'Nunito Sans', sans-serif;
  line-height: 1.5;

  @media (max-width: 900px) {
    width: 220px;
  }
`;

const YearBadge = styled.span`
  background: #eeeeee;
  color: #444444;
  padding: 0 8px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  font-family: 'Nunito Sans', sans-serif;
  animation: ${(props) => (props.hasChanged ? 'fadeIn 0.4s ease' : 'none')};

  @keyframes fadeIn {
    0% {
      opacity: 0;
      transform: scale(0.8);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const TableRow = styled.div`
  display: flex;
  width: 100%;
  min-height: 56px;
  position: relative;
  page-break-inside: avoid;
  break-inside: avoid;
  scroll-margin-top: 41px;
`;

const WeekCell = styled.div`
  width: 210px;
  min-width: 140px;
  padding: 8px;
  background: #ffffff;
  border-right: 1px solid #eeeeee;
  border-left: 1px solid #eeeeee;
  border-top: 1px solid #eeeeee;
  border-bottom: ${(props) => (props.isLast ? '1px solid #EEEEEE' : '0')};
  display: flex;
  gap: 16px;
  align-items: center;
  position: relative;

  @media (max-width: 900px) {
    width: 160px;
    gap: 8px;
  }
`;

const WeekNumber = styled.div`
  background: ${(props) => (props.isCurrent ? '#444444' : 'transparent')};
  color: ${(props) => (props.isCurrent ? '#FFFFFF' : '#444444')};
  padding: 0 8px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: ${(props) => (props.isCurrent ? '700' : '400')};
  line-height: 1.5;
  font-family: 'Nunito Sans', sans-serif;
`;

const WeekDates = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #444444;
  line-height: 1.5;
  font-family: 'Nunito Sans', sans-serif;
`;

const BlocksCell = styled.div`
  flex: 1;
  min-height: 56px;
  min-width: 0;
  padding: 4px;
  background: #ffffff;
  border-right: 1px solid #eeeeee;
  border-top: 1px solid #eeeeee;
  border-bottom: ${(props) => (props.isLast ? '1px solid #EEEEEE' : '0')};
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ReviewBlock = styled.div`
  min-height: 48px;
  padding: 8px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: 0;
  color: #444444;
  background: ${(props) => props.colors.background};
  border: 1px solid ${(props) => props.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: normal;
  word-break: break-word;
  font-family: 'Nunito Sans', sans-serif;
  flex-shrink: 1;
  max-width: 100%;
  box-sizing: border-box;
`;

const NotesCell = styled.button`
  width: 300px;
  min-width: 200px;
  min-height: 56px;
  padding: 8px 4px;
  background: #ffffff;
  border: 1px solid transparent;
  border-top: 1px solid #eeeeee;
  border-bottom: ${(props) => (props.isLast ? '1px solid #EEEEEE' : '1px solid transparent')};
  border-right: 1px solid #eeeeee;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 4px;
  text-align: left;
  cursor: pointer;
  font-family: 'Nunito Sans', sans-serif;
  position: relative;
  box-sizing: border-box;

  @media (max-width: 900px) {
    width: 220px;
  }

  &:hover {
    background: rgba(119, 167, 255, 0.2);
    border: 2px solid #77a7ff;
    border-top: 2px solid #77a7ff;
    border-bottom: ${(props) => (props.isLast ? '2px solid #77A7FF' : '2px solid #77A7FF')};
    border-right: 2px solid #77a7ff;
    padding: 7px 3px;
  }

  &:hover::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 24px;
    height: 24px;
    background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16 12H8M12 8V16' stroke='%2377A7FF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    pointer-events: none;
    opacity: ${(props) => (props.hasNotes ? 0 : 1)};
  }
`;

const NotesIconContainer = styled.div`
  width: 16px;
  height: 16px;
  min-width: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoteText = styled.p`
  margin: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #444444;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: 'Nunito Sans', sans-serif;
  flex: 1;
  min-width: 0;
`;

const CurrentWeekIndicator = styled.div`
  position: absolute;
  left: -4px;
  right: 0;
  top: -1px;
  height: 3px;
  pointer-events: none;
  z-index: 15;
  background: #FF0000;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    width: 8px;
    height: 8px;
    background: #FF0000;
    border-radius: 50%;
    transform: translate(0, -50%);
    z-index: 20;
  }
`;

const LoadingIndicator = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  background: #77A7FF;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: ${(props) => (props.show ? '1' : '0')};
  visibility: ${(props) => (props.show ? 'visible' : 'hidden')};
  transition: opacity 0.2s ease, visibility 0.2s ease;
`;

const EmptyReviewState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 80px 24px;
  text-align: center;
  color: rgba(15, 23, 42, 0.6);

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #444444;
  }

  p {
    margin: 0;
    font-size: 14px;
    max-width: 360px;
    color: rgba(15, 23, 42, 0.45);
  }
`;

const OfflineBanner = styled.div`
  background: #e74c3c;
  color: white;
  padding: 12px 20px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Nunito Sans', sans-serif;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;

  &::before {
    content: '⚠️';
    font-size: 16px;
  }
`;

/**
 * ReviewView Component
 *
 * Displays the review mode with vertical timeline layout and notes.
 * Blocks are shown vertically by week, repeated for each week they span.
 * Row position determines left-to-right order.
 */
const ReviewView = ({ selectedCalendarId, weeks }) => {
  const dispatch = useDispatch();
  const surfaceRef = useRef(null);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);

  const blocksMap = useSelector((state) => state.planner.data.blocks);
  const blocks = useMemo(() => Object.values(blocksMap), [blocksMap]);
  const notes = useSelector((state) => state.planner.data.notes);
  const isLoadingMoreWeeks = useSelector((state) => state.planner.ui.isLoadingMoreWeeks);
  const loadedWeekRange = useSelector((state) => state.planner.ui.loadedWeekRange);
  const currentWeekString = getCurrentWeek();
  const { isOnline } = useNetworkStatus();

  // Use extracted scroll hook
  const { timelineRef, currentYear, previousYear } = useReviewViewScroll({
    weeks,
    loadedWeekRange,
    isLoadingMoreWeeks,
  });

  // Memoize blocks by week with optimized lookups (O(n) instead of O(n²))
  const blocksByWeek = useMemo(() => {
    const result = {};
    const weekIndexMap = new Map();

    // Build week index map once (O(n))
    weeks.forEach((week, index) => {
      weekIndexMap.set(week, index);
      result[week] = [];
    });

    // Filter blocks for each week efficiently (O(n*m) where m is blocks)
    blocks.forEach((block) => {
      const fromIndex = weekIndexMap.get(block.fromWeek);
      const toIndex = weekIndexMap.get(block.toWeek);

      if (fromIndex === undefined || toIndex === undefined) return;

      // Add block to all weeks it spans
      for (let i = fromIndex; i <= toIndex; i += 1) {
        const week = weeks[i];
        if (week) {
          result[week].push(block);
        }
      }
    });

    // Sort each week's blocks by row once
    Object.keys(result).forEach((week) => {
      result[week].sort((a, b) => a.row - b.row);
    });

    return result;
  }, [blocks, weeks]);

  // Get blocks for a specific week from memoized cache
  const getBlocksForWeek = useCallback(
    (week) => blocksByWeek[week] || [],
    [blocksByWeek],
  );

  const handleOpenNotesModal = useCallback((week) => {
    setSelectedWeek(week);
    setIsNotesModalOpen(true);
  }, []);

  const handleCloseNotesModal = useCallback(() => {
    setIsNotesModalOpen(false);
    setSelectedWeek(null);
  }, []);

  // Save note by dispatching Redux action to persist it
  const handleSaveNote = useCallback((week, noteText) => {
    dispatch(upsertNote(week, noteText));
  }, [dispatch]);

  if (!selectedCalendarId) {
    return (
      <ReviewViewContainer>
        <ReviewSurface>
          <EmptyReviewState>
            <h3>Select a calendar to review plans</h3>
            <p>
              The timeline will show each planned block alongside your weekly
              notes.
            </p>
          </EmptyReviewState>
        </ReviewSurface>
      </ReviewViewContainer>
    );
  }

  if (!weeks.length) {
    return (
      <ReviewViewContainer>
        <ReviewSurface>
          <EmptyReviewState>
            <h3>No weeks in view</h3>
            <p>
              Update the date window above to see your planned blocks and notes.
            </p>
          </EmptyReviewState>
        </ReviewSurface>
      </ReviewViewContainer>
    );
  }

  return (
    <ReviewViewContainer>
      {!isOnline && (
        <OfflineBanner>
          You are currently offline. Viewing only - changes cannot be saved.
        </OfflineBanner>
      )}
      <ReviewSurface ref={surfaceRef}>
        <TimelineScrollContainer ref={timelineRef}>
          <TimelineContainer>
            <TableHeader data-table-header>
              <WeekHeaderCell>
                <span>Week</span>
                <YearBadge hasChanged={previousYear !== null}>
                  {currentYear}
                </YearBadge>
              </WeekHeaderCell>
              <BlocksHeaderCell>Blocks</BlocksHeaderCell>
              <NotesHeaderCell>Notes</NotesHeaderCell>
            </TableHeader>

            <TableBody>
              {weeks.map((week, index) => {
                const isCurrent = week === currentWeekString;
                const isLast = index === weeks.length - 1;
                const weekMatch = week.match(/^(\d{4})-W(\d{2})$/);
                const weekNumber = weekMatch ? parseInt(weekMatch[2], 10) : '';
                const dateRange = formatWeekDateRangeCompact(week);
                const weekBlocks = getBlocksForWeek(week);
                const noteText = notes[week]?.text;

                return (
                  <TableRow key={week} data-week-row={week}>
                    {isCurrent && <CurrentWeekIndicator />}
                    <WeekCell isCurrent={isCurrent} isLast={isLast}>
                      <WeekNumber isCurrent={isCurrent}>
                        {`W${weekNumber}`}
                      </WeekNumber>
                      <WeekDates>{dateRange}</WeekDates>
                    </WeekCell>

                    <BlocksCell isCurrent={isCurrent} isLast={isLast}>
                      {weekBlocks.length > 0
                        && weekBlocks.map((block) => (
                          <ReviewBlock
                            key={`${week}-${block.id}`}
                            colors={getColorOptionForBlock(block)}
                          >
                            {block.name || 'Untitled'}
                          </ReviewBlock>
                        ))}
                    </BlocksCell>

                    <NotesCell
                      isCurrent={isCurrent}
                      isLast={isLast}
                      hasNotes={!!noteText}
                      type='button'
                      onClick={() => handleOpenNotesModal(week)}
                      aria-label={noteText ? 'Edit notes' : 'Add notes'}
                    >
                      <NotesIconContainer>
                        <IcomoonReact
                          iconSet={iconSet}
                          icon='note'
                          size={12}
                          color='#9e9e9e'
                        />
                      </NotesIconContainer>
                      {noteText && <NoteText>{noteText}</NoteText>}
                    </NotesCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </TimelineContainer>
        </TimelineScrollContainer>
      </ReviewSurface>

      {isNotesModalOpen && selectedWeek && (
        <NotesModal
          week={selectedWeek}
          initialNote={notes[selectedWeek]?.text}
          onSave={handleSaveNote}
          onClose={handleCloseNotesModal}
        />
      )}

      <LoadingIndicator show={isLoadingMoreWeeks}>
        Loading more weeks...
      </LoadingIndicator>
    </ReviewViewContainer>
  );
};

ReviewView.propTypes = {
  selectedCalendarId: PropTypes.string,
  weeks: PropTypes.arrayOf(PropTypes.string).isRequired,
};

ReviewView.defaultProps = {
  selectedCalendarId: null,
};

export default ReviewView;
